import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { Eye } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { SourceCode } from "../../SourceCode";
import { ExpandableMessageCard, FileName } from "./shared";
import { useFileLinkProps } from "./useFileLinkProps";

interface ViewTextProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}

export function ViewText({ part, messageId }: ViewTextProps) {
  const path = part.args?.path as string;
  const viewRange = part.args?.view_range as number[] | undefined;

  const chatId = useActiveChatId();
  const linkProps = useFileLinkProps(chatId, messageId, path || "");

  // Extract file content from result
  const fileContent =
    part.result &&
    typeof part.result === "object" &&
    "data" in part.result &&
    typeof part.result.data === "string"
      ? part.result.data
      : "";

  // Determine if we're viewing a specific range
  const isViewingRange = viewRange && viewRange.length === 2;
  const startLine = isViewingRange ? viewRange[0] : 1;
  const endLine = isViewingRange
    ? viewRange[1] === -1
      ? "end"
      : viewRange[1]
    : "end";

  const rangeText = isViewingRange ? ` (lines ${startLine}-${endLine})` : "";

  const title = (
    <>
      Viewing
      <FileName filename={path} linkProps={linkProps} variant="outline" />
      {rangeText}
    </>
  );

  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Eye}
      title={title}
      className="min-w-0 mb-0"
      accordionValue="view-text"
    >
      {fileContent ? (
        <SourceCode
          content={fileContent}
          language={getLanguageFromPath(path)}
          className="max-h-96 overflow-auto"
          lineNumberOffset={startLine - 1}
        />
      ) : (
        <div className="text-muted-foreground text-sm">
          No content to display
        </div>
      )}
    </ExpandableMessageCard>
  );
}

// Helper function to determine language from file path
function getLanguageFromPath(path: string): string {
  const extension = path.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "ts":
    case "tsx":
      return "ts";
    case "json":
      return "json";
    case "md":
      return "md";
    default:
      return "text";
  }
}
